import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config';
import { colors } from '../theme/colors';

const Login = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password
      });

      const { access_token } = response.data;

      // Store token in localStorage
      localStorage.setItem('lipoti_token', access_token);

      // Set default axios header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Call success callback
      onLoginSuccess();
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t('auth.loginError'));
      } else {
        setError(t('auth.loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.background.sidebar,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 5,
          maxWidth: 420,
          width: '100%',
          mx: 2,
          borderRadius: '16px',
          border: `1px solid ${colors.border.light}`,
          bgcolor: colors.background.white,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: colors.background.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <Lock sx={{ fontSize: 32, color: colors.ui.accent }} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: colors.text.primary,
              mb: 1,
            }}
          >
            {t('auth.welcomeTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary }}
          >
            {t('auth.welcomeSubtitle')}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('auth.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            autoFocus
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.light,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.ui.accent,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.light,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.ui.accent,
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: colors.text.muted }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                borderRadius: '8px',
                bgcolor: `${colors.status.error}10`,
                color: colors.status.error,
                '& .MuiAlert-icon': {
                  color: colors.status.error,
                },
              }}
            >
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.5,
              bgcolor: colors.ui.button,
              color: colors.background.white,
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: colors.ui.buttonHover,
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: colors.text.muted,
                color: colors.background.white,
              },
            }}
          >
            {loading ? t('common.loading') : t('auth.login')}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default Login;